import { Grid, Paper, Typography } from "@mui/material";
import CardImage from "@/components/cardImage";
import { PurchaseResultTango } from "@t3/shared";
import { extractRedemptionInfoWithFallback } from "@/utils/extractRedemptionInfo";
import { RedemptionSection, ResendData } from "./RedemptionSection";

export const CartHistoryTangoItem = ({
  product,
  resendData,
}: {
  product: PurchaseResultTango;
  resendData?: ResendData;
}) => {
  // Extract redemption information with backward compatibility
  // New data has redemptionInstructions, old data has rawResponse.reward
  const legacyProductInstructions =
    (typeof product.tangoProduct.metadata?.items?.[0]
      ?.redemptionInstructions === "string"
      ? product.tangoProduct.metadata.items[0].redemptionInstructions
      : null) ||
    (typeof product.tangoProduct.metadata?.redemptionInstructions === "string"
      ? product.tangoProduct.metadata.redemptionInstructions
      : null);

  const redemptionInfo = extractRedemptionInfoWithFallback(
    product.redemptionInstructions,
    product.rawResponse?.reward,
    legacyProductInstructions,
  );

  return (
    <Paper
      elevation={2}
      sx={{
        margin: "16px 0",
        padding: 2,
        borderRadius: 1.5,
      }}
      key={product.tangoProduct.utid}
      data-cypress={`history-tango-item`}
    >
      <Grid container spacing={2} direction={{ xs: "column", sm: "row" }}>
        {/* Product Image */}
        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
          <div
            style={{
              width: "100%",
              maxWidth: "180px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              borderRadius: "6px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}
            data-cypress="tango-item-image-container"
          >
            <CardImage
              imageUrl={product.tangoProduct.metadata.imageUrls["300w-326ppi"]}
              margin={{ xs: "5px 5px 0 3px", sm: "auto" }}
              width="100%"
              height="auto"
            />
          </div>
        </Grid>

        {/* Product Details */}
        <Grid size={{ xs: 12, sm: 9, md: 10 }}>
          {/* Product Title */}
          {product.tangoProduct.metadata.brandName &&
            `$${product.tangoProduct.amount} - ${product.tangoProduct.metadata.brandName}`}

          {/* Delivery Email Address */}
          {product.email ? (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "#555",
                fontWeight: 500,
              }}
              data-cypress="tango-item-delivery-email"
            >
              Delivery email address:{" "}
              <span
                style={{ fontWeight: "normal" }}
                data-cypress="tango-item-email-value"
              >
                {product.email}
              </span>
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "#555",
                fontWeight: 500,
              }}
              data-cypress="tango-item-delivery-email-error"
            >
              Delivery email address:{" "}
              <span style={{ fontWeight: "normal" }}>
                Not available due to error
              </span>
            </Typography>
          )}

          {/* Error display for item in refunded state */}
          {(product.status === "refunded" || product.status === "refund") && (
            <Typography
              variant="caption"
              sx={{
                color: "#d32f2f",
                p: 1,
                bgcolor: "rgba(211, 47, 47, 0.05)",
                borderRadius: 1,
                border: "1px solid rgba(211, 47, 47, 0.1)",
                fontWeight: "normal",
                mt: 1,
                display: "block",
              }}
              data-cypress="tango-item-error-message"
            >
              An error occurred on this purchase. Your points have been
              refunded. Try purchasing this product again.
            </Typography>
          )}

          {/* Reference ID and Points */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: 10,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#555",
                "& span": {
                  fontWeight: 500,
                  color: "#333",
                  wordBreak: "break-all",
                },
              }}
              data-cypress="tango-item-reference-id"
            >
              Reference ID:{" "}
              <span data-cypress="tango-item-reference-id-value">
                {product.referenceOrderID}
              </span>
            </Typography>

            <Typography
              variant="body2"
              sx={{ fontWeight: 500 }}
              data-cypress="tango-item-points"
            >
              Points:{" "}
              <span
                style={{ color: "#d32f2f", fontWeight: "bold" }}
                data-cypress="tango-item-points-value"
              >
                {product.pointsCost}
              </span>
            </Typography>
          </div>
        </Grid>
      </Grid>

      {/* Redemption Section - Only show if purchase was successful (not refunded) */}
      {product.status === "completed" && (
        <RedemptionSection
          redemptionInfo={redemptionInfo}
          resendData={resendData}
        />
      )}
    </Paper>
  );
};
